import { MedplumClient, parseReference, QueryTypes } from "@medplum/core";
import { DiagnosticReport, Observation } from "@medplum/fhirtypes";

import chunk from "lodash/chunk";

import { DiagnosticServiceSectionID } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import {
  fetchPregnancyRecordForDiagnosticReport,
  fetchPregnancyRecordForLabOrder,
  fetchPregnancyRecords,
} from "@/pregnancy";
import {
  getCodeableConceptCode,
  getCodeableConceptDisplay,
  getResourcesFromBundle,
  joinReferenceString,
  joinTokenString,
} from "@/utils/medplum";

import { LabFlowSheetData, LabFlowSheetSection, LabFlowSheetSectionMap } from "../types/flow-sheet";
import { HealthGorillaCodes } from "../types/labs";
import {
  getLabFlowSheetSubsectionKey,
  getLabFlowSheetSubsectionLabel,
  getLabObservationResultDisplay,
  getLabOrderResultRangeDisplay,
} from "../utils/labs";

export async function fetchLabFlowSheet(
  medplum: MedplumClient,
  clientId: string,
): Promise<LabFlowSheetData> {
  const { collectionDates, resultIdByCollectionDate, edd } =
    await fetchLabFlowSheetDiagnosticReports(medplum, clientId);

  const [validSections, allSubsectionsIds] = await getLabFlowSheetValidSections(
    medplum,
    resultIdByCollectionDate,
  );

  if (
    resultIdByCollectionDate.size === 0 ||
    allSubsectionsIds.size === 0 ||
    validSections.length === 0
  ) {
    return { collectionDates: [], sections: [], edd };
  }

  const subsectionObservationsById = await mapSubsectionsObservations(medplum, allSubsectionsIds);

  // A Map is being used here so we can group the sections by code to avoid duplicating them.
  // This way we can group all values of the same type of lab test together.
  const sectionsMap = new Map<string, LabFlowSheetSectionMap>();
  for (const { observation, collectionDate } of validSections) {
    const sectionMap = buildLabFlowSheetSectionMap(observation, collectionDate, sectionsMap);
    buildLabFlowSheetSubsections(
      observation,
      collectionDate,
      subsectionObservationsById,
      sectionMap,
    );
  }

  const sortedSectionCodes = Array.from(sectionsMap.keys()).sort((a, b) => {
    const dateA = sectionsMap.get(a)!.earliestDate;
    const dateB = sectionsMap.get(b)!.earliestDate;
    return dateA.localeCompare(dateB);
  });

  const sections: LabFlowSheetSection[] = sortedSectionCodes.map((sectionCode) => {
    const sectionMap = sectionsMap.get(sectionCode)!;
    return {
      name: sectionMap.name,
      code: sectionCode,
      subsections: Array.from(sectionMap.subsectionsMap.values()),
    };
  });

  return {
    collectionDates: Array.from(collectionDates).sort(),
    sections,
    edd,
  };
}

async function fetchLabFlowSheetDiagnosticReports(
  medplum: MedplumClient,
  clientId: string,
): Promise<{
  collectionDates: Set<string>;
  resultIdByCollectionDate: Map<string, string>;
  edd: string | undefined;
}> {
  // Fetch all reports first so we can derive the correct EDD from their linked
  // ServiceRequests. The LMP filter is applied in-memory afterwards.
  const { bundle } = await medplum.searchResources("DiagnosticReport", {
    subject: joinReferenceString("Patient", clientId),
    _sort: "issued",
    category: joinTokenString(
      Systems.HL7DiagnosticServiceSectionID,
      DiagnosticServiceSectionID.Laboratory,
    ),
    _count: 1000,
  } satisfies QueryTypes);

  const allDiagnosticReports = getResourcesFromBundle("DiagnosticReport", bundle);

  const edd = await resolveFlowSheetEdd(medplum, clientId, allDiagnosticReports);

  // TODO: Uncomment this when we implement the Episode of Care boundary

  // // Apply the LMP boundary as an in-memory filter so that only reports issued
  // // on or after the start of the relevant pregnancy are included.
  // const lmpBoundary = edd
  //   ? formatISO(deriveLastMenstrualPeriodFromEDD(edd), { representation: "date" })
  //   : undefined;

  const collectionDates = new Set<string>();
  const resultIdByCollectionDate = new Map<string, string>();

  for (const report of allDiagnosticReports) {
    if (!report.issued || !report.result) continue;

    collectionDates.add(report.issued);
    for (const result of report.result) {
      resultIdByCollectionDate.set(parseReference(result)[1], report.issued);
    }
  }

  return { collectionDates, resultIdByCollectionDate, edd };
}

/**
 * Resolves the EDD for the flowsheet in three steps:
 *
 * 1. Preferred: find the pregnancy record linked to a ServiceRequest referenced
 *    in `DiagnosticReport.basedOn`. This gives an accurate EDD even when the
 *    pregnancy is no longer active (e.g. the patient has already delivered).
 *
 * 2. Intermediate fallback: find the pregnancy record whose `List.entry`
 *    directly contains one of the provided DiagnosticReports. This covers
 *    manual and uploaded results that were linked via
 *    `upsertDiagnosticReportToPregnancyRecord` instead of a ServiceRequest.
 *
 * 3. Final fallback: use the currently active pregnancy record's EDD for
 *    reports that pre-date Phase 2 or have no pregnancy link of any kind.
 */
async function resolveFlowSheetEdd(
  medplum: MedplumClient,
  clientId: string,
  diagnosticReports: DiagnosticReport[],
): Promise<string | undefined> {
  // Step 1 — ServiceRequest-based lookup
  const serviceRequestId = diagnosticReports
    .flatMap((report) => report.basedOn ?? [])
    .map((reference) => parseReference(reference))
    .find(([resourceType]) => resourceType === "ServiceRequest")?.[1];

  if (serviceRequestId) {
    const record = await fetchPregnancyRecordForLabOrder(medplum, clientId, serviceRequestId);
    if (record?.edd) return record.edd;
  }

  // Step 2 — DiagnosticReport-based lookup (direct List.entry link)
  for (const report of diagnosticReports) {
    if (!report.id) continue;
    const record = await fetchPregnancyRecordForDiagnosticReport(medplum, clientId, report.id);
    if (record?.edd) return record.edd;
  }

  // Step 3 — Active pregnancy fallback
  const activeRecords = await fetchPregnancyRecords(medplum, clientId, { status: "current" });
  return activeRecords[0]?.edd;
}

async function getLabFlowSheetValidSections(
  medplum: MedplumClient,
  resultIdByCollectionDate: Map<string, string>,
): Promise<[Array<{ observation: Observation; collectionDate: string }>, Set<string>]> {
  const allSectionObservations = await fetchLabFlowSheetObservationsInChunks(
    medplum,
    Array.from(resultIdByCollectionDate.keys()),
  );

  const validSections: Array<{ observation: Observation; collectionDate: string }> = [];
  const allSubsectionsIds = new Set<string>();

  for (const observation of allSectionObservations) {
    if (!observation.id) continue;
    if (getCodeableConceptCode(observation.code) === HealthGorillaCodes.ClinicalPDFReport) continue;

    const collectionDate = resultIdByCollectionDate.get(observation.id);
    if (!collectionDate) continue;

    validSections.push({ observation, collectionDate });
    observation.hasMember?.forEach((member) => allSubsectionsIds.add(parseReference(member)[1]));
  }

  return [validSections, allSubsectionsIds];
}

async function fetchLabFlowSheetObservationsInChunks(
  medplum: MedplumClient,
  ids: string[],
  size: number = 100,
): Promise<Observation[]> {
  if (ids.length === 0) return [];

  const chunks = chunk(ids, size);
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const { bundle } = await medplum.searchResources("Observation", {
        _id: chunk.join(","),
        _count: chunk.length,
      });
      return getResourcesFromBundle("Observation", bundle);
    }),
  );

  return results.flat();
}

async function mapSubsectionsObservations(
  medplum: MedplumClient,
  allSubsectionsIds: Set<string>,
): Promise<Map<string, Observation>> {
  const allSubsectionObservations = await fetchLabFlowSheetObservationsInChunks(
    medplum,
    Array.from(allSubsectionsIds),
  );
  const subsectionObservationsById = new Map<string, Observation>();
  for (const observation of allSubsectionObservations) {
    if (observation.id) subsectionObservationsById.set(observation.id, observation);
  }

  return subsectionObservationsById;
}

function buildLabFlowSheetSectionMap(
  sectionObservation: Observation,
  collectionDate: string,
  sectionsMap: Map<string, LabFlowSheetSectionMap>,
) {
  const sectionCode = getCodeableConceptCode(sectionObservation.code);
  const sectionName =
    sectionObservation.code?.text || sectionObservation.code?.coding?.[0]?.display || sectionCode;

  if (!sectionsMap.has(sectionCode)) {
    sectionsMap.set(sectionCode, {
      name: sectionName,
      subsectionsMap: new Map(),
      earliestDate: collectionDate,
    });
  }

  return sectionsMap.get(sectionCode)!;
}

function buildLabFlowSheetSubsections(
  sectionObservation: Observation,
  collectionDate: string,
  subsectionObservationsById: Map<string, Observation>,
  sectionMap: LabFlowSheetSectionMap,
): void {
  // Update earliest date if this collection date is earlier
  // This date is used so we can sort the sections by earliest collection date,
  // which helps us avoid displaying sections with no data at the beginning of the table.
  if (collectionDate < sectionMap.earliestDate) {
    sectionMap.earliestDate = collectionDate;
  }

  for (const memberReference of sectionObservation.hasMember ?? []) {
    const memberId = parseReference(memberReference)[1];
    const subsectionObservation = subsectionObservationsById.get(memberId);
    if (!subsectionObservation) continue;

    const subsectionCode = getCodeableConceptCode(subsectionObservation.code);
    const subsectionName = getLabFlowSheetSubsectionLabel(subsectionObservation);
    const subsectionKey = getLabFlowSheetSubsectionKey(subsectionObservation);

    if (!sectionMap.subsectionsMap.has(subsectionKey)) {
      sectionMap.subsectionsMap.set(subsectionKey, {
        name: subsectionName,
        code: subsectionCode,
        unit: subsectionObservation.valueQuantity?.unit ?? "",
        referenceRange: getLabOrderResultRangeDisplay(subsectionObservation),
        values: {},
      });
    }

    const subsection = sectionMap.subsectionsMap.get(subsectionKey)!;
    const value = getLabObservationResultDisplay(subsectionObservation);
    const interpretation =
      subsectionObservation.interpretation?.[0]?.text ||
      getCodeableConceptDisplay(subsectionObservation.interpretation?.[0]) ||
      "";
    subsection.values[collectionDate] = value || interpretation;

    if (!subsection.unit && subsectionObservation.valueQuantity?.unit) {
      subsection.unit = subsectionObservation.valueQuantity.unit;
    }
    if (!subsection.referenceRange && subsectionObservation.referenceRange?.length) {
      subsection.referenceRange = getLabOrderResultRangeDisplay(subsectionObservation);
    }
  }
}
